"use client";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import Image from "next/image";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/autoplay";
import { partners } from "@/types/blog";

export default function Carousel({ swiperData }: { swiperData: partners[] }) {
  return (
    <div className="relative mx-auto w-full max-w-5xl px-4 sm:px-6 py-10 md:py-12 bg-gray-50 dark:bg-gray-900">
      {/* Navigation buttons */}
      <button
        type="button"
        className="swiper-button-prev absolute top-1/2 -translate-y-1/2 left-2 sm:left-4 z-10 h-10 w-10 rounded-full bg-white/90 text-gray-800 shadow-md backdrop-blur-sm transition hover:bg-white dark:bg-gray-800/90 dark:text-gray-200 dark:hover:bg-gray-800 flex items-center justify-center"
        aria-label="Previous slide"
      >
        <span className="sr-only">Previous</span>
      </button>

      <button
        type="button"
        className="swiper-button-next absolute top-1/2 -translate-y-1/2 right-2 sm:right-4 z-10 h-10 w-10 rounded-full bg-white/90 text-gray-800 shadow-md backdrop-blur-sm transition hover:bg-white dark:bg-gray-800/90 dark:text-gray-200 dark:hover:bg-gray-800 flex items-center justify-center"
        aria-label="Next slide"
      >
        <span className="sr-only">Next</span>
      </button>

      <Swiper
        modules={[Navigation, Pagination]}
        spaceBetween={16}
        slidesPerView={1}
        navigation={{
          nextEl: ".swiper-button-next",
          prevEl: ".swiper-button-prev",
        }}
        pagination={{ clickable: true, dynamicBullets: true }}
        centeredSlides={true}
        className="partner-swiper"
      >
        {swiperData.map((item, index) => (
          <SwiperSlide key={item.id ?? index}>
            <div className="mx-auto max-w-2xl">
              <div className="group relative overflow-hidden rounded-xl bg-white shadow-lg transition-all duration-300 hover:shadow-xl dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                <div className="flex flex-col items-center px-6 pb-8 pt-8 md:px-8 md:pt-10 md:pb-8 text-center">
                  {/* Smaller Profile Image */}
                  <div className="mb-6 transform transition-transform duration-400 group-hover:scale-105">
                    <div className="relative inline-block rounded-full border-4 border-white dark:border-gray-700 shadow-lg ring-1 ring-gray-200/50 dark:ring-gray-600/40">
                      <Image
                        src={item.image}
                        alt={`${item.title} - ${item.designation}`}
                        width={140}
                        height={140}
                        className="h-32 w-32 rounded-full object-cover object-top sm:h-36 sm:w-36"
                        priority={index < 2}
                      />
                    </div>
                  </div>

                  {/* Content - more compact */}
                  <div className="space-y-4 max-w-prose">
                    <div className="space-y-1">
                      <h3 className="font-serif text-2xl sm:text-3xl font-medium text-gray-900 dark:text-gray-100 tracking-tight">
                        {item.title}
                      </h3>
                      <p className="text-base sm:text-lg font-medium text-gray-600 dark:text-gray-400">
                        {item.designation}
                      </p>
                    </div>

                    <p className="text-sm sm:text-base leading-relaxed text-gray-700 dark:text-gray-300 whitespace-pre-line">
                      {item.paragraph}
                    </p>

                    <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 text-xs sm:text-sm space-y-2 text-gray-600 dark:text-gray-400">
                      {item.email && (
                        <p>
                          <span className="font-semibold text-gray-800 dark:text-gray-200">Email:</span>{" "}
                          <a
                            href={`mailto:${item.email}`}
                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition"
                          >
                            {item.email}
                          </a>
                        </p>
                      )}

                      {item.linkedInURL && (
                        <p>
                          <span className="font-semibold text-gray-800 dark:text-gray-200">LinkedIn:</span>{" "}
                          <a
                            href={item.linkedInURL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline transition"
                          >
                            View Profile
                          </a>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Pagination Styles */}
      <style jsx global>{`
        .partner-swiper .swiper-pagination-bullet {
          background: rgba(107, 114, 128, 0.6);
          opacity: 1;
          width: 8px;
          height: 8px;
        }
        .partner-swiper .swiper-pagination-bullet-active {
          background: #1f2937;
          width: 24px;
          border-radius: 9999px;
        }
        @media (prefers-color-scheme: dark) {
          .partner-swiper .swiper-pagination-bullet-active {
            background: #e5e7eb;
          }
        }
        .partner-swiper .swiper-pagination {
          bottom: 8px !important;
        }
      `}</style>
    </div>
  );
}